import {noop} from '@github-ui/noop'
import {CheckIcon} from '@primer/octicons-react'
import {Box, IconButton, Text} from '@primer/react'
import {useCallback, useEffect, useState} from 'react'

type FieldWrapperProps = {
  name: string
  placeholder: string
  value?: React.ReactNode
  input?: React.ReactNode
  inputRef?: React.RefObject<HTMLInputElement>
  anchorProps?: React.HTMLAttributes<HTMLElement>
  canUpdate?: boolean
}

type MetadataButtonProps = {
  disabled: boolean
  children: React.ReactNode
  onClick: React.MouseEventHandler<HTMLButtonElement>
  anchorProps?: React.HTMLAttributes<HTMLButtonElement>
}

export function MetadataButton({children, onClick, anchorProps, disabled}: MetadataButtonProps) {
  const sx = disabled
    ? {}
    : {
        '&:hover': {
          backgroundColor: 'canvas.subtle',
        },
      }

  return (
    <Box
      as={disabled ? undefined : 'button'}
      onClick={disabled ? noop : onClick}
      sx={{
        border: 'none',
        borderRadius: 2,
        backgroundColor: 'transparent',
        textAlign: 'left',
        width: '100%',
        height: 'auto',
        overflow: 'hidden',
        minWidth: 0,
        px: 2,
        py: 2,
        display: 'flex',
        flexDirection: 'row',
        gap: 3,
        alignItems: 'flex-start',
        color: 'fg.muted',
        ...sx,
      }}
      {...anchorProps}
    >
      <Box sx={{display: 'flex', fontSize: 1, flexGrow: 1, wordBreak: 'break-word', overflow: 'hidden'}}>
        {children}
      </Box>
    </Box>
  )
}

export function FieldWrapper({
  name,
  placeholder,
  value,
  input,
  inputRef,
  anchorProps,
  canUpdate = true,
}: FieldWrapperProps) {
  const [showInput, setShowInput] = useState(false)

  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      if (!anchorProps) {
        setShowInput(true)
      } else if (anchorProps?.onClick) {
        anchorProps.onClick(e)
      }
    },
    [anchorProps],
  )

  useEffect(() => {
    if (showInput === true && inputRef && inputRef.current) {
      inputRef.current.focus()
    }
  }, [inputRef, showInput])

  return (
    <Box as="li" sx={{mt: 0, mx: 2}}>
      <Box sx={{display: 'flex', flexDirection: 'row', alignItems: 'flex-start', gap: 3, width: '100%'}}>
        <Text
          as="p"
          sx={{
            fontWeight: 500,
            fontSize: 0,
            color: 'fg.muted',
            marginBottom: 0,
            wordBreak: 'break-word',
            width: '100px',
            flexShrink: 0,
            mt: 2,
            ml: 2,
          }}
        >
          {name}
        </Text>
        {showInput ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              gap: 1,
              mt: '3px',
              mb: 1,
              ml: '-5px',
            }}
          >
            {input}
            <IconButton
              sx={{flexShrink: 0}}
              aria-label="Cancel"
              size="small"
              icon={CheckIcon}
              onClick={() => setShowInput(false)}
              disabled={!canUpdate}
            />
          </Box>
        ) : (
          <MetadataButton anchorProps={anchorProps} onClick={handleClick} disabled={!canUpdate}>
            {value ? value : <Text sx={{color: 'fg.muted'}}>{placeholder}</Text>}
          </MetadataButton>
        )}
      </Box>
    </Box>
  )
}

try{ MetadataButton.displayName ||= 'MetadataButton' } catch {}
try{ FieldWrapper.displayName ||= 'FieldWrapper' } catch {}